/**
 * Admin dues hold helpers. node --test
 *
 * A dues hold means "no new rides or trips for this Anna until he has paid what he
 * owes". It is NOT Block: a blocked Anna's whole app is replaced by a "blocked" screen
 * (he can't see or pay his dues), while a held Anna keeps full use of the app so he
 * can pay. The server (firebase/functions/adminDriverDispatch.js, setDriverDuesHold)
 * is the authority; this file only decides what to offer and what to say.
 *
 * The field exists on the driver document only while the hold is on, with state
 * 'active'. A recorded payment that clears his dues lifts it automatically.
 */

export const isDuesHeld = (driver) => String(driver?.duesHold?.state || '') === 'active';

export const owedAmount = (driver) => Math.max(0, Number(driver?.pendingDues) || 0);

/** Offer "Hold for dues" only for an approved, unblocked Anna who owes something and is not already held. */
export const canHoldForDues = (driver) => Boolean(driver)
  && driver.isApproved === true
  && driver.isBlocked !== true
  && !isDuesHeld(driver)
  && owedAmount(driver) > 0;

export function duesHoldConfirmText(driver, action) {
  const name = driver?.name || 'this Anna';
  const owed = owedAmount(driver);
  if (action === 'release') {
    return `Release the dues hold on ${name}? He will be able to go online and take rides again, even though he still owes ₹${owed}.`;
  }
  const onTrip = Boolean(driver?.activeRideId || driver?.isOnIntertownTrip);
  return `Put ${name} on a dues hold? He owes ₹${owed}. He will get no new rides or trips and cannot go online until he pays. `
    + 'He can still use the app, including Earnings and Pay dues. '
    + (onTrip
      ? 'He is on a trip right now: it will not be interrupted.'
      : 'He will be taken offline now.')
    + ' The hold lifts by itself when you record a payment that clears his dues.';
}

/** What the admin sees after the server answers, by what it actually did. */
export function duesHoldResultText(driver, result) {
  const name = driver?.name || 'Anna';
  switch (result?.mode) {
    case 'offline': return `${name} is on a dues hold and has been taken offline`;
    case 'after_trip': return `${name} is on a dues hold. His current trip continues, and he gets no new rides after it`;
    case 'released': return `Dues hold released for ${name}`;
    case 'already_held': return `${name} is already on a dues hold`;
    case 'not_held': return `${name} was not on a dues hold`;
    default: return `Dues hold updated for ${name}`;
  }
}

const SERVER_MESSAGE_CODES = new Set([
  'functions/failed-precondition',
  'functions/permission-denied',
  'functions/invalid-argument',
  'functions/not-found',
]);

/** The server's own plain refusal ("This Anna owes nothing…") is shown as is; anything else gets a generic line. */
export function duesHoldErrorText(error) {
  const message = String(error?.message || '').trim();
  if (SERVER_MESSAGE_CODES.has(String(error?.code || '')) && message) return message;
  return 'Could not update the dues hold';
}
